<script setup lang="ts">
import { UiText, UiIcon, UiChip, UiTooltip } from "@shopexperts/shared";
import type { AllocationSourceRow } from "./useAdminWalletTransaction";

withDefaults(
  defineProps<{
    rows: AllocationSourceRow[];
    unallocatedLabel?: string | null;
    heading?: string;
    headingTooltip?: string;
    /** Drop the section's own padding when embedded in an already-padded card. */
    flush?: boolean;
    /** What the unmatched warning calls this transaction, e.g. "debit" or "payout". */
    subject?: string;
    /** Replaces the default unmatched warning sentence; `unallocatedLabel` is still the trigger. */
    unallocatedMessage?: string | null;
    /** Omit the heading row when the parent already labels the section (e.g. an accordion trigger). */
    hideHeading?: boolean;
  }>(),
  {
    unallocatedLabel: null,
    heading: "Funded by",
    headingTooltip:
      "The released holds and credits whose available funds covered this debit, matched oldest first.",
    flush: false,
    subject: "debit",
    unallocatedMessage: null,
    hideHeading: false,
  },
);
</script>

<template>
  <section class="border-t border-se-gray-200" :class="flush ? 'pt-3' : 'px-4 py-4'">
    <div v-if="!hideHeading" class="flex items-center gap-1.5">
      <UiText as="h4" variant="p-small" :text="heading" class="font-semibold text-se-dark" />
      <UiTooltip :content="headingTooltip">
        <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-500" />
      </UiTooltip>
    </div>

    <ul v-if="rows.length" class="flex flex-col" :class="hideHeading ? '' : 'mt-3'">
      <li
        v-for="row in rows"
        :key="row.key"
        class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-dashed border-se-gray-200 py-2 first:pt-0 last:border-b-0 last:pb-0"
      >
        <div class="flex min-w-0 flex-wrap items-center gap-1.5">
          <UiText
            as="span"
            variant="small"
            :text="row.sourceLabel"
            class="font-medium text-se-dark"
          />
          <UiTooltip v-if="row.holdTransactionId !== null" content="The hold this release came from">
            <UiChip
              :text="`Hold #${row.holdTransactionId}`"
              variant="plain"
              size="xs"
              rounded="full"
            />
          </UiTooltip>
          <UiTooltip v-if="row.isPartialConsumption && row.partialTooltip" :content="row.partialTooltip">
            <UiChip text="Partial" variant="warn" size="xs" rounded="full" />
          </UiTooltip>
          <template v-for="reference in row.references" :key="reference.key">
            <RouterLink
              v-if="reference.to"
              :to="reference.to"
              class="inline-flex max-w-full rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark"
              :aria-label="`Open ${reference.label}`"
            >
              <UiChip :text="reference.label" variant="neutral" size="xs" rounded="full" icon="link-external" />
            </RouterLink>
            <UiChip v-else :text="reference.label" variant="neutral" size="xs" rounded="full" />
          </template>
        </div>
        <div class="flex shrink-0 items-baseline gap-1">
          <UiText
            as="span"
            variant="small"
            :text="row.amountLabel"
            class="font-semibold text-se-dark"
          />
          <UiText
            v-if="row.sourceTotalLabel"
            as="span"
            variant="caption-normal"
            :text="row.sourceTotalLabel"
            class="text-se-gray-500"
          />
        </div>
      </li>
    </ul>

    <div
      v-if="unallocatedLabel"
      class="mt-3 flex items-center gap-2 rounded-md bg-se-warn-light px-3 py-2"
    >
      <UiIcon name="info-circle" class="h-4 w-4 shrink-0 text-se-warn" />
      <UiText
        as="span"
        variant="small"
        :text="unallocatedMessage ?? `${unallocatedLabel} of this ${subject} has no recorded funding source. Reconciling the wallet can close the gap once matching credits exist.`"
        class="text-se-dark"
      />
    </div>
  </section>
</template>
